import { generatePath, Link, useLocation, useNavigate } from "react-router-dom";
import React, { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { APP_ROUTES } from "../router/constants";
import { ApplicationState } from "../reducers";
import {
  breadcrumbType,
  isCustomBreadcrumbs,
} from "../../components/organisms/PageHeader/constants";
import { preparePath } from "../helpers/helpfulFunctions";

export const usePageNavigate = ({
  lastCustomCrumb,
}: {
  lastCustomCrumb?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location?.pathname;

  const isCalendarsNestedRoute = currentPath.includes("/calendars/");
  const isCustomersNestedRoute = currentPath.includes("/customers/");
  const isGamificationNestedRoute = currentPath.includes("/gamification/");
  const isPointGamesNestedRoute = currentPath.includes("/point-games/");

  const breadcrumbsArr = useSelector(
    (state: ApplicationState) => state.breadcrumbs,
    shallowEqual
  );

  const navigateBack = useCallback(() => {
    if (isCalendarsNestedRoute) {
      navigate(APP_ROUTES.calendars, { state: { from: currentPath } });
    } else if (isGamificationNestedRoute) {
      if (isPointGamesNestedRoute) {
        const gamificationId = currentPath.split("/")[2];

        navigate(
          generatePath(APP_ROUTES.pointGames, {
            gamificationId,
          }),
          { state: { from: currentPath } }
        );
      } else {
        navigate(APP_ROUTES.gamification, { state: { from: currentPath } });
      }
    } else if (isCustomersNestedRoute && !location.state) {
      navigate(APP_ROUTES.customers);
    } else navigate(-1);
  }, [
    currentPath,
    navigate,
    location,
    isCustomersNestedRoute,
    isCalendarsNestedRoute,
    isGamificationNestedRoute,
    isPointGamesNestedRoute,
  ]);

  const crumbsArr = useMemo(
    () => location.pathname.split("/").filter((item) => !!item),
    [location.pathname]
  );

  const crumbsRoutes = useMemo(
    () =>
      isCustomBreadcrumbs(currentPath)
        ? breadcrumbsArr
        : crumbsArr.map(
            (crumb): breadcrumbType => ({
              title: crumb,
              path: `/${crumb}`,
            })
          ),
    [currentPath, breadcrumbsArr, crumbsArr]
  );

  const renderCrumbs = useCallback(
    (currentRoute, params, items, paths) => {
      const isLast = currentRoute?.path === items[items.length - 1]?.path;

      return isLast ? (
        <span className="crumbs-label">
          {lastCustomCrumb || currentRoute.title}
        </span>
      ) : (
        <Link
          className="crumbs-link"
          to={`/${preparePath(paths.join("/"))}`}
          state={{ from: currentRoute.root ? "" : currentPath }}
        >
          {currentRoute.title}
        </Link>
      );
    },
    [lastCustomCrumb, currentPath]
  );

  return { crumbsRoutes, renderCrumbs, navigateBack };
};
